package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.intelligence.ConciergeHandoff
import com.steamstreet.vegasful.browser.appScope
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.Element
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.asList
import kotlin.js.json
import kotlin.math.PI
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.min
import kotlin.math.round
import kotlin.math.sin
import kotlin.random.Random

/**
 * The home page hero (`home-hero`): the neon headline, the city lights behind it, and the switch
 * that turns the search box into the concierge.
 *
 * The server renders a complete, working hero; this module only adds to it. The searching itself
 * stays with `global-search`, which reads the `data-mode` and `data-when` this module keeps on
 * the box. See `specs/web-home-hero.md`.
 */
fun homeHero(element: Element) {
    // A visitor who asked the concierge before signing in lands back here after signing in,
    // because that is where sign-in returns. Their question is waiting, so take them to it.
    if (Account.isLoggedIn() && ConciergeHandoff.isHeld()) {
        window.location.href = ConciergeHandoff.CONCIERGE_PATH
        return
    }
    HomeHero(element as HTMLElement).start()
}

/** Everything the canvas needs to paint one sky: two colours, and how much night is in it. */
private class Sky(val mid: DoubleArray, val horizon: DoubleArray, val stars: Double, val city: Double) {
    fun copy() = Sky(mid.copyOf(), horizon.copyOf(), stars, city)
}

private class Light(
    var x: Double, val y: Double, val r: Double, val colour: DoubleArray, val alpha: Double,
    val phase: Double, val speed: Double, val vx: Double, val takesAccent: Boolean
)

private class SkyStar(val x: Double, val y: Double, val size: Double, val phase: Double, val speed: Double)

private class Spark(
    var x: Double, var y: Double, var vx: Double, var vy: Double,
    var life: Double, val max: Double, val size: Double, val colour: String
)

private class HomeHero(private val root: HTMLElement) {
    private val canvas = root.querySelector(".vf-sky") as? HTMLCanvasElement
    private val ctx = canvas?.getContext("2d") as? CanvasRenderingContext2D
    private val searchBox = root.querySelector("[data-module=global-search]") as? HTMLElement
    private val input = root.querySelector(".vf-input") as? HTMLInputElement
    private val bell = root.querySelector(".vf-bell") as? HTMLElement
    private val go = root.querySelector(".vf-go") as? HTMLElement
    private val whenButton = root.querySelector(".vf-when") as? HTMLElement
    private val neon = root.querySelector(".vf-neon") as? HTMLElement
    private val star = root.querySelector(".vf-star") as? HTMLElement
    private val ring = root.querySelector(".vf-ring")
    private val neonBox = root.querySelector(".vf-neonbox") as? HTMLElement

    private val reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    private val searchPlaceholder = input?.placeholder ?: ""

    private var width = 0.0
    private var height = 0.0
    private var lights = listOf<Light>()
    private var stars = listOf<SkyStar>()
    private val sparks = mutableListOf<Spark>()

    private var current = SKIES.getValue("night").copy()
    private var target = current.copy()
    private var accent = doubleArrayOf(255.0, 79.0, 245.0)
    private var accentTarget = 0.0
    private var accentAmount = 0.0

    private var frameId = 0
    private var previous = 0.0
    private var clock = 12.0
    private var visible = true

    private var words = listOf<HTMLElement>()
    private var widths = listOf<Double>()
    private var currentWord = 0
    private var flipping = false
    private val timers = mutableListOf<Int>()

    private var whenIndex = 0
    private var hintJob: Job? = null

    fun start() {
        buildWords()
        current = SKIES.getValue(phaseOf(vegasNow())).copy()
        target = current.copy()

        sizeRing()
        root.classList.add("vf-ready")
        resize()
        observe()

        bell?.addEventListener("click", {
            setMode(if (root.getAttribute("data-mode") == "ask") "search" else "ask")
            input?.focus()
            if (root.getAttribute("data-mode") == "ask") burst(bell)
        })
        whenButton?.addEventListener("click", {
            whenIndex = (whenIndex + 1) % 3
            drawWhen()
        })
        neon?.addEventListener("click", { flip() })
        drawWhen()

        // The sky follows the real hour, so it is checked again every minute.
        window.setInterval({
            if (!flipping) setSky(phaseOf(vegasNow()))
            if (whenIndex == 0) drawWhen()
        }, 60_000)

        if (reducedMotion) {
            drawStill()
        } else {
            startLoop()
            flip()
        }
    }

    // ---------------------------------------------------------------- the concierge switch

    private fun setMode(mode: String) {
        root.setAttribute("data-mode", mode)
        searchBox?.setAttribute("data-mode", mode)
        val asking = mode == "ask"
        bell?.setAttribute("aria-pressed", asking.toString())
        input?.setAttribute("aria-label", if (asking) "Ask the concierge" else "Search Vegasful")
        go?.textContent = if (asking) "Ask →" else "Search"

        hintJob?.cancel()
        if (asking) {
            startHints()
        } else {
            input?.placeholder = searchPlaceholder
        }
        if (reducedMotion) drawStill()
    }

    /** The concierge's own example questions, typed into the placeholder one after another. */
    private fun startHints() {
        val field = input ?: return
        if (reducedMotion) {
            field.placeholder = HINTS.first()
            return
        }
        hintJob = appScope.launch {
            while (isActive) {
                for (hint in HINTS) {
                    // While the visitor is in the box the example holds still, whole, so it reads
                    // as a suggestion rather than something moving under their cursor.
                    fun interrupted() = document.activeElement == field || field.value.isNotBlank()
                    while (interrupted()) {
                        field.placeholder = if (field.value.isBlank()) hint else ""
                        delay(300)
                    }
                    for (i in 1..hint.length) {
                        if (interrupted()) break
                        field.placeholder = hint.substring(0, i)
                        delay(45)
                    }
                    if (interrupted()) continue
                    delay(1800)
                    for (i in field.placeholder.length downTo 0) {
                        if (interrupted()) break
                        field.placeholder = field.placeholder.substring(0, i)
                        delay(18)
                    }
                    delay(400)
                }
            }
        }
    }

    /**
     * The box's time frame. Its first setting is "Tonight" in the evening and "Today" before
     * that, by the Las Vegas clock rather than the visitor's.
     */
    private fun drawWhen() {
        val first = if (isTonight(vegasNow())) "Tonight" else "Today"
        val label = listOf(first, "This weekend", "Any day")[whenIndex]
        whenButton?.textContent = "$label ▾"
        whenButton?.setAttribute("aria-label", "When: $label. Press to change.")
        searchBox?.setAttribute("data-when", if (whenIndex == 2) "" else label.lowercase())
    }

    // ---------------------------------------------------------------- the neon tube

    private fun sizeRing() {
        val box = neonBox ?: return
        val svg = ring ?: return
        val rect = box.getBoundingClientRect()
        val w = rect.width + 12
        val h = rect.height + 12
        svg.setAttribute("viewBox", "0 0 $w $h")
        svg.querySelectorAll("rect").asList().forEach {
            (it as Element).apply {
                setAttribute("x", "2")
                setAttribute("y", "2")
                setAttribute("width", "${max(1.0, w - 4)}")
                setAttribute("height", "${max(1.0, h - 4)}")
                setAttribute("rx", "19")
            }
        }
    }

    // ---------------------------------------------------------------- the neon headline

    /**
     * Add the flip words in front of the headline's real last word. They are built here rather
     * than sent by the server so that the page, and anything reading it, only ever says the real
     * headline; they are hidden from assistive technology for the same reason.
     */
    private fun buildWords() {
        val container = neon ?: return
        val final = container.querySelector(".vf-word") as? HTMLElement ?: return
        val extra = container.getAttribute("data-words").orEmpty().split(",").map { it.trim() }.filter { it.isNotEmpty() }
        val built = extra.map { word ->
            (document.createElement("span") as HTMLElement).apply {
                className = "vf-word"
                setAttribute("data-c", word.lowercase())
                setAttribute("aria-hidden", "true")
                textContent = word
                container.insertBefore(this, final)
            }
        }
        words = built + final
        currentWord = words.size - 1
        measureWords()
        // The words are measured again once the web font has arrived, or the first widths would
        // be the fallback face's.
        val fonts = document.asDynamic().fonts
        if (fonts != null && fonts != undefined) {
            fonts.ready.then({ _: dynamic -> measureWords() }, { _: dynamic -> })
        }
    }

    private fun measureWords() {
        widths = words.map { it.getBoundingClientRect().width }
        widths.getOrNull(currentWord)?.takeIf { it > 0 }?.let { neon?.style?.width = "${it}px" }
    }

    private fun later(ms: Int, block: () -> Unit) {
        timers += window.setTimeout(block, ms)
    }

    private fun clearTimers() {
        timers.forEach { window.clearTimeout(it) }
        timers.clear()
    }

    /**
     * Cycle the neon word once and land on the real one. Each change is a sign switching: the old
     * word cuts out, the unlit glass of the next shows for a moment, then it buzzes on. The sky
     * and about half the lights take on each word's colour as it lights.
     */
    private fun flip() {
        if (reducedMotion || words.size < 2) return
        clearTimers()
        flipping = true
        root.classList.add("flipping")
        val last = words.size - 1
        var t = 250
        for (i in 0..last) {
            later(t) {
                val old = words[currentWord]
                if (currentWord != i) {
                    old.classList.remove("buzz")
                    old.classList.add("out")
                }
            }
            later(t + 140) {
                words.forEachIndexed { j, w ->
                    w.classList.remove("out", "buzz", "lit", "glass")
                    if (j == i) w.classList.add("glass")
                }
                currentWord = i
                widths.getOrNull(i)?.let { neon?.style?.width = "${it}px" }
                val name = words[i].getAttribute("data-c").orEmpty()
                if (i < last && SKIES.containsKey(name)) {
                    setSky(name)
                    ACCENTS[name]?.let { accent = it; accentTarget = 1.0 }
                } else {
                    setSky(phaseOf(vegasNow()))
                    accentTarget = 0.0
                }
            }
            later(t + 360) {
                val w = words[i]
                w.classList.remove("glass")
                w.getBoundingClientRect()
                w.classList.add("lit", "buzz")
                if (i == last) land()
            }
            t += 1150
        }
    }

    private fun land() {
        flipping = false
        later(250) { root.classList.remove("flipping") }
        later(420) { star?.let { burst(it) } }
    }

    // ---------------------------------------------------------------- the sky and the lights

    private fun setSky(name: String) {
        target = SKIES[name]?.copy() ?: return
        if (reducedMotion) drawStill()
    }

    private fun resize() {
        val c = canvas ?: return
        val context = ctx ?: return
        val rect = root.getBoundingClientRect()
        width = rect.width
        height = rect.height
        val dpr = min(window.devicePixelRatio, 2.0)
        c.width = max(1, round(width * dpr).toInt())
        c.height = max(1, round(height * dpr).toInt())
        context.setTransform(dpr, 0.0, 0.0, dpr, 0.0, 0.0)

        val random = Random(11)
        val count = max(16.0, min(38.0, width / 30)).toInt()
        val scale = if (width < 640) .65 else 1.0
        lights = (0 until count).map { i ->
            val depth = random.nextDouble()
            Light(
                x = random.nextDouble() * width,
                y = height * (.42 + random.nextDouble() * .6),
                r = (7 + depth * depth * 44) * scale,
                colour = PALETTE[i % PALETTE.size],
                alpha = .16 + (1 - depth) * .26,
                phase = random.nextDouble() * 2 * PI,
                speed = .25 + random.nextDouble() * .6,
                vx = (random.nextDouble() - .5) * (4 + depth * 8),
                takesAccent = random.nextDouble() < .45
            )
        }
        stars = (0 until 60).map {
            SkyStar(
                random.nextDouble() * width, random.nextDouble() * height * .45,
                if (random.nextDouble() < .15) 2.0 else 1.3,
                random.nextDouble() * 2 * PI, .6 + random.nextDouble() * 1.8
            )
        }
        measureWords()
        sizeRing()
        if (reducedMotion || frameId == 0) drawStill()
    }

    private fun drawStill() {
        current = target.copy()
        accentAmount = accentTarget
        paint(clock, 0.0)
    }

    private fun paint(t: Double, dt: Double) {
        val c = ctx ?: return
        val k = min(1.0, dt * 2)
        for (i in 0..2) {
            current.mid[i] += (target.mid[i] - current.mid[i]) * k
            current.horizon[i] += (target.horizon[i] - current.horizon[i]) * k
        }
        current = Sky(current.mid, current.horizon,
            current.stars + (target.stars - current.stars) * k,
            current.city + (target.city - current.city) * k)
        accentAmount += (accentTarget - accentAmount) * min(1.0, dt * 2.5)

        c.globalCompositeOperation = "source-over"
        c.globalAlpha = 1.0
        c.clearRect(0.0, 0.0, width, height)

        // The colour of the hour builds gradually from the top down. The top is the brand purple
        // tinted with the hour, dark enough for the white headline but not black, and the horizon
        // is softened towards the mid colour so the band behind the box is a glow, not a stripe.
        //
        // The sky never sits still: the colours drift slowly through the brand's neon palette and
        // the glow breathes, with the hour (or the lit word, during the flip) as a tint on top.
        val ambient = ambientColour(t)
        val pulse = .84 + .16 * sin(t * .7)
        val mid = mix(current.mid, mix(ambient, BRAND, .45), .5)
        val top = mix(BRAND, mid, .42)
        val horizon = scale(mix(mix(current.horizon, mid, .25), ambient, .6), pulse)
        val sky = c.createLinearGradient(0.0, 0.0, 0.0, height)
        sky.addColorStop(0.0, rgb(top))
        sky.addColorStop(.32, rgb(mix(top, mid, .45)))
        sky.addColorStop(.6, rgb(mix(mid, horizon, .55)))
        sky.addColorStop(.8, rgb(horizon))
        sky.addColorStop(1.0, rgb(mix(horizon, GROUND, .6)))
        c.fillStyle = sky
        c.fillRect(0.0, 0.0, width, height)

        if (current.stars > .02) {
            c.fillStyle = "#fff"
            stars.forEach { s ->
                c.globalAlpha = current.stars * (.15 + .5 * (.5 + .5 * sin(t * s.speed + s.phase)))
                c.fillRect(s.x, s.y, s.size, s.size)
            }
            c.globalAlpha = 1.0
        }

        // Soft, out-of-focus city lights, brighter at the rim the way a lens draws them.
        c.globalCompositeOperation = "lighter"
        lights.forEach { light ->
            light.x += light.vx * dt
            if (light.x < -light.r) light.x = width + light.r else if (light.x > width + light.r) light.x = -light.r
            // The lights that take an accent follow the drifting sky at rest and the lit word
            // during the flip, blending from one to the other as the flip starts and lands.
            val colour = if (light.takesAccent) {
                mix(light.colour, mix(ambient, accent, accentAmount), .45 + .4 * accentAmount)
            } else {
                light.colour
            }
            val a = light.alpha * (.3 + .7 * current.city) * (.72 + .28 * sin(t * light.speed + light.phase))
            val y = light.y + sin(t * .2 + light.phase) * 4
            val g = c.createRadialGradient(light.x, y, 0.0, light.x, y, light.r)
            g.addColorStop(0.0, rgba(colour, a * .55))
            g.addColorStop(.72, rgba(colour, a * .5))
            g.addColorStop(.9, rgba(colour, a * .95))
            g.addColorStop(1.0, rgba(colour, 0.0))
            c.fillStyle = g
            c.beginPath()
            c.arc(light.x, y, light.r, 0.0, 2 * PI)
            c.fill()
        }
        c.globalCompositeOperation = "source-over"

        // Fade into the page, so the hero meets the panels below it without an edge. The fade is
        // long and eased, so it reads as the light falling off rather than a shutter coming down.
        val fadeTop = height * .62
        val fade = c.createLinearGradient(0.0, fadeTop, 0.0, height)
        fade.addColorStop(0.0, rgba(GROUND, 0.0))
        fade.addColorStop(.55, rgba(GROUND, .35))
        fade.addColorStop(1.0, rgba(GROUND, 1.0))
        c.fillStyle = fade
        c.fillRect(0.0, fadeTop, width, height - fadeTop)

        c.globalCompositeOperation = "lighter"
        paintSparks(c, dt)
        c.globalCompositeOperation = "source-over"
    }

    private fun paintSparks(c: CanvasRenderingContext2D, dt: Double) {
        val iterator = sparks.iterator()
        while (iterator.hasNext()) {
            val s = iterator.next()
            s.life -= dt
            if (s.life <= 0) {
                iterator.remove()
                continue
            }
            s.vy += 150 * dt
            s.x += s.vx * dt
            s.y += s.vy * dt
            s.vx *= .985
            val size = s.size * (.5 + s.life / s.max)
            c.globalAlpha = min(1.0, s.life / s.max * 1.4)
            c.fillStyle = s.colour
            c.beginPath()
            c.moveTo(s.x, s.y - size)
            c.quadraticCurveTo(s.x, s.y, s.x + size, s.y)
            c.quadraticCurveTo(s.x, s.y, s.x, s.y + size)
            c.quadraticCurveTo(s.x, s.y, s.x - size, s.y)
            c.quadraticCurveTo(s.x, s.y, s.x, s.y - size)
            c.fill()
        }
        c.globalAlpha = 1.0
    }

    /** A small burst of sparkles from [from]: the headline landing, or the concierge turning on. */
    private fun burst(from: Element) {
        if (reducedMotion) return
        val r = from.getBoundingClientRect()
        val h = root.getBoundingClientRect()
        val x = r.left + r.width / 2 - h.left
        val y = r.top + r.height / 2 - h.top
        repeat(28) { i ->
            val angle = Random.nextDouble() * 2 * PI
            val speed = 70 + Random.nextDouble() * 180
            val life = .7 + Random.nextDouble() * .6
            sparks += Spark(
                x, y, kotlin.math.cos(angle) * speed, sin(angle) * speed - 40, life, life,
                2 + Random.nextDouble() * 3.2, SPARK_COLOURS[i % SPARK_COLOURS.size]
            )
        }
        startLoop()
    }

    private fun startLoop() {
        if (reducedMotion || frameId != 0 || !visible || document.asDynamic().hidden == true) return
        previous = window.performance.now()
        frameId = window.requestAnimationFrame(::frame)
    }

    private fun stopLoop() {
        if (frameId != 0) window.cancelAnimationFrame(frameId)
        frameId = 0
    }

    private fun frame(now: Double) {
        frameId = window.requestAnimationFrame(::frame)
        val dt = min(.05, (now - previous) / 1000).takeIf { it > 0 } ?: .016
        previous = now
        clock += dt
        paint(clock, dt)
    }

    /**
     * Keep the canvas sized to the hero, and stop painting whenever nobody can see it: the tab is
     * in the background, or the hero has scrolled away.
     */
    private fun observe() {
        val reflect = js("Reflect")
        val resizeObserver = window.asDynamic().ResizeObserver
        if (resizeObserver != undefined) {
            reflect.construct(resizeObserver, arrayOf<dynamic>({ _: dynamic -> resize() })).observe(root)
            neonBox?.let { box ->
                reflect.construct(resizeObserver, arrayOf<dynamic>({ _: dynamic -> sizeRing() })).observe(box)
            }
        } else {
            window.addEventListener("resize", { resize() })
        }

        val intersectionObserver = window.asDynamic().IntersectionObserver
        if (intersectionObserver != undefined) {
            reflect.construct(intersectionObserver, arrayOf<dynamic>({ entries: dynamic ->
                visible = entries[0].isIntersecting as Boolean
                if (visible) startLoop() else stopLoop()
            })).observe(root)
        }
        document.addEventListener("visibilitychange", {
            if (document.asDynamic().hidden == true) stopLoop() else startLoop()
        })
    }
}

// -------------------------------------------------------------------- Las Vegas time

/** The time in Las Vegas, as a month, a day of the month and minutes after midnight. */
private class VegasTime(val month: Int, val day: Int, val minutes: Int)

private val vegasClock: dynamic = js(
    "new Intl.DateTimeFormat('en-US', {timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hourCycle: 'h23'})"
)

private fun vegasNow(): VegasTime {
    val parts = mutableMapOf<String, String>()
    val list = vegasClock.formatToParts(js("new Date()"))
    for (i in 0 until (list.length as Int)) {
        parts[list[i].type as String] = list[i].value as String
    }
    val hour = (parts["hour"]?.toIntOrNull() ?: 0) % 24
    return VegasTime(
        (parts["month"]?.toIntOrNull() ?: 1) - 1,
        parts["day"]?.toIntOrNull() ?: 15,
        hour * 60 + (parts["minute"]?.toIntOrNull() ?: 0)
    )
}

/**
 * Approximate Las Vegas sunrise and sunset on the 15th of each month, in local minutes after
 * midnight. Near enough to colour a sky; nothing here is shown as a time.
 */
private val SUNRISE = intArrayOf(411, 395, 420, 380, 340, 321, 331, 355, 379, 403, 374, 402)
private val SUNSET = intArrayOf(1019, 1051, 1137, 1161, 1184, 1200, 1199, 1176, 1134, 1092, 997, 989)

private fun sunTimes(time: VegasTime): Pair<Int, Int> {
    val position = time.month + (time.day - 15) / 30.0
    val first = floor(position).toInt()
    val fraction = position - first
    val a = ((first % 12) + 12) % 12
    val b = (a + 1) % 12
    fun between(values: IntArray) = (values[a] + (values[b] - values[a]) * fraction).toInt()
    return between(SUNRISE) to between(SUNSET)
}

private fun phaseOf(time: VegasTime): String {
    val (rise, set) = sunTimes(time)
    val m = time.minutes
    return when {
        m < rise - 45 -> "late"
        m < rise + 60 -> "dawn"
        m < set - 100 -> "day"
        m < set - 20 -> "golden"
        m < set + 25 -> "sunset"
        else -> "night"
    }
}

private fun isTonight(time: VegasTime) = phaseOf(time) in setOf("late", "night", "sunset", "golden")

// -------------------------------------------------------------------- palettes

private fun sky(mid: IntArray, horizon: IntArray, stars: Double, city: Double) =
    Sky(mid.map { it.toDouble() }.toDoubleArray(), horizon.map { it.toDouble() }.toDoubleArray(), stars, city)

/**
 * The skies: the hours of the day, and one for each flip word. Only the lower part of the hero
 * takes these colours.
 */
private val SKIES = mapOf(
    "night" to sky(intArrayOf(34, 22, 104), intArrayOf(70, 46, 168), 1.0, .9),
    "sunset" to sky(intArrayOf(132, 36, 110), intArrayOf(246, 108, 104), .15, .6),
    "dinner" to sky(intArrayOf(100, 26, 112), intArrayOf(255, 88, 118), .45, .85),
    "weekend" to sky(intArrayOf(96, 12, 124), intArrayOf(232, 40, 178), .8, 1.0),
    "dawn" to sky(intArrayOf(76, 54, 144), intArrayOf(240, 150, 168), .15, .35),
    "day" to sky(intArrayOf(62, 78, 176), intArrayOf(150, 180, 244), 0.0, .3),
    "golden" to sky(intArrayOf(116, 50, 120), intArrayOf(236, 128, 132), 0.0, .5),
    "late" to sky(intArrayOf(28, 12, 78), intArrayOf(104, 24, 122), 1.0, 1.0)
)

/** The colour each flip word lends the lights while it is lit. */
private val ACCENTS = mapOf(
    "night" to doubleArrayOf(57.0, 198.0, 255.0),
    "sunset" to doubleArrayOf(255.0, 138.0, 61.0),
    "dinner" to doubleArrayOf(255.0, 63.0, 108.0),
    "weekend" to doubleArrayOf(61.0, 255.0, 154.0)
)

private val PALETTE = listOf(
    doubleArrayOf(255.0, 194.0, 122.0), doubleArrayOf(255.0, 111.0, 232.0), doubleArrayOf(111.0, 220.0, 255.0),
    doubleArrayOf(169.0, 139.0, 255.0), doubleArrayOf(255.0, 111.0, 143.0), doubleArrayOf(255.0, 214.0, 160.0)
)

/** The page background, which the hero fades into. */
private val GROUND = doubleArrayOf(21.0, 1.0, 54.0)

/** The brand purple the top of the sky starts from. */
private val BRAND = doubleArrayOf(36.0, 10.0, 84.0)

private val SPARK_COLOURS = listOf("#ff7dfe", "#ffffff", "#99ebff", "#ffd36b")

/** The concierge's example questions, the same ones its own page uses. */
private val HINTS = listOf(
    "What’s happening tonight?",
    "Any rooftop shows this weekend?",
    "Find me something low-key and fun.",
    "Where’s a good comedy show?",
    "Show me what’s electric in Vegas tonight."
)

/**
 * The brand colours the sky drifts through, one full cycle every [AMBIENT_CYCLE_S] seconds.
 */
private val AMBIENT = listOf(
    doubleArrayOf(206.0, 46.0, 196.0),
    doubleArrayOf(118.0, 64.0, 224.0),
    doubleArrayOf(56.0, 118.0, 232.0),
    doubleArrayOf(236.0, 72.0, 146.0)
)
private const val AMBIENT_CYCLE_S = 20.0

/** Where the drift is at [t] seconds, eased between neighbouring colours. */
private fun ambientColour(t: Double): DoubleArray {
    val position = (t / AMBIENT_CYCLE_S * AMBIENT.size) % AMBIENT.size
    val index = floor(position).toInt()
    val fraction = position - index
    val eased = fraction * fraction * (3 - 2 * fraction)
    return mix(AMBIENT[index], AMBIENT[(index + 1) % AMBIENT.size], eased)
}

private fun scale(c: DoubleArray, k: Double) = doubleArrayOf(min(255.0, c[0] * k), min(255.0, c[1] * k), min(255.0, c[2] * k))

private fun mix(a: DoubleArray, b: DoubleArray, k: Double) =
    doubleArrayOf(a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k)

private fun rgb(c: DoubleArray) = "rgb(${c[0].toInt()},${c[1].toInt()},${c[2].toInt()})"

private fun rgba(c: DoubleArray, a: Double) =
    "rgba(${c[0].toInt()},${c[1].toInt()},${c[2].toInt()},${(a * 1000).toInt() / 1000.0})"
